import { useMemo, useRef, useState } from "react";
import { useEnglishText } from "../../text/useEnglishText.js";
import { X } from "lucide-react";
import "../../styles/List.css";

import DocumentsTable from "../ui/DocumentsTable";
import { Button } from "../ui/button";
import EmptyState from "../common/EmptyState";
import LoadingState from "../common/LoadingState";

import { sortData } from "../../utils/sortData";
import { filterData } from "../../utils/filterData";
import { extractColumns } from "../../utils/extractColumns";

export default function DocumentsList({
  data = [],
  loading = false,
  onSelect,
  page = 1,
  onPageChange,
  pageSize = 10,
  total,
  paginateLocally = false,
  showSelection = true,
  error = "",
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyIcon,
  actions,
}) {
  const { t } = useEnglishText();
  const scrollRef = useRef(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({});
  const updateFilters = (next) => {
    setFilters(next);
    if (paginateLocally && page !== 1) onPageChange?.(1);
  };

  const resolvedEmptyTitle = emptyTitle || t("common.noRecords", "No records found");
  const resolvedEmptyDescription = emptyDescription || t("common.noRecordsDescription", "There are no records to display yet.");

  const filtered = filterData(data, filters);
  const sorted = sortData(filtered, sort);
  const totalPages = paginateLocally
    ? Math.max(1, Math.ceil(sorted.length / pageSize))
    : total == null ? null : Math.max(1, Math.ceil(total / pageSize));
  const currentPage = totalPages == null ? page : Math.min(page, totalPages);
  const visibleRows = paginateLocally ? sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize) : sorted;

  const layout = (
    <MainLayout
      sorted={visibleRows}
      columnsData={data}
      scrollRef={scrollRef}
      filters={filters}
      setFilters={updateFilters}
      sort={sort}
      setSort={setSort}
      onSelect={onSelect}
      showSelection={showSelection}
      emptyTitle={resolvedEmptyTitle}
      emptyDescription={resolvedEmptyDescription}
      emptyAction={emptyAction}
      emptyIcon={emptyIcon}
      loading={loading}
      error={error}
      actions={actions}
    />
  );

  // -----------------------------
  // MODE 1: NO TOTAL → SIMPLE PAGINATION
  // -----------------------------
  if (total == null && !paginateLocally) {
    const hasNextPage = data.length === pageSize;
    const showPagination = !loading && !error && sorted.length > 0 && (page > 1 || hasNextPage);

    const goToPage = (p) => {
      if (p < 1) return;
      onPageChange?.(p);
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
      <section className="documents-list-section">
        {layout}

        {showPagination && (
          <div className="pagination pagination-mobile">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className="pagination-btn"
            >
              {t("common.back")}
            </Button>

            <div className="pagination-pages">
              <span className="pagination-item">{page}</span>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(page + 1)}
              disabled={!hasNextPage}
              className="pagination-btn"
            >
              {t("common.next")}
            </Button>
          </div>
        )}
      </section>
    );
  }

  // -----------------------------
  // MODE 2: HAS TOTAL → FULL PAGINATION
  // -----------------------------
  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    onPageChange?.(p);
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pages = createPageNumbers(currentPage, totalPages);

  return (
    <section className="documents-list-section">
      {layout}

      {!loading && !error && sorted.length > 0 && totalPages > 1 && <div className="pagination pagination-numbered">
        <Button type="button" variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
          {t("common.back")}
        </Button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={i} className="pagination-ellipsis">...</span>
          ) : (
            <Button
              type="button"
              variant={currentPage === p ? "secondary" : "outline"}
              size="sm"
              key={i}
              className={`pagination-item ${currentPage === p ? "active" : ""}`}
              onClick={() => goToPage(p)}
            >
              {p}
            </Button>
          )
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t("common.next")}
        </Button>
      </div>}
    </section>
  );
}

// -------------------------------------------------
// PAGE NUMBER GENERATOR LIKE OLD COMPONENT
// -------------------------------------------------
function createPageNumbers(current, total) {
  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  if (current <= 3) {
    return [1, 2, 3, 4, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 3, total - 2, total - 1, total];
  }

  return [1, "...", current - 1, current, current + 1, "...", total];
}

// -------------------------------------------------
// WRAPPED MAIN CONTENT (برای کم شدن تکرار JSX)
// -------------------------------------------------
function MainLayout({
  sorted,
  columnsData,
  scrollRef,
  filters,
  setFilters,
  sort,
  setSort,
  onSelect,
  showSelection,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyIcon,
  loading,
  error,
  actions,
}) {
  const { t } = useEnglishText();
  const columnLabels = useMemo(
    () => new Map(extractColumns(columnsData).map((column) => [column.key, column.label])),
    [columnsData],
  );
  const hasFilters = Object.values(filters).some(hasFilterValue);

  return (
    <div
      className="documents-layout ltr"
    >
      <div className="documents-list-wrapper">
        {(actions || hasFilters) && (
          <div className="documents-list-toolbar" dir="ltr">
            <div className="documents-list-actions">
              {actions}
              {hasFilters && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="documents-clear-filters"
                  onClick={() => setFilters({})}
                >
                  {t("common.clearAll", "Clear all filters")}
                </Button>
              )}
            </div>
          </div>
        )}
        {hasFilters && (
          <div className="documents-filter-chips" aria-label={t("common.activeFilters", "Active filters")}>
            {Object.entries(filters).filter(([, value]) => hasFilterValue(value)).map(([key, value]) => {
              const label = columnLabels.get(key) || key;
              return (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="documents-filter-chip"
                  aria-label={`${t("common.clear", "Clear")} ${label}: ${String(value)}`}
                  onClick={() => setFilters((current) => {
                    const next = { ...current };
                    delete next[key];
                    return next;
                  })}
                >
                  <span>{label}</span><strong>{String(value)}</strong><X size={14} aria-hidden="true" />
                </Button>
              );
            })}
          </div>
        )}
        <div ref={scrollRef} className="documents-list">
          {loading ? (
            <LoadingState variant="table" label={t("common.loading", "Loading records")} />
          ) : error ? (
            <div className="admin-data-error" role="alert">{error}</div>
          ) : sorted.length === 0 ? (
            <EmptyState
              icon={emptyIcon}
              title={hasFilters ? t("common.noMatchingRecords", "No matching records") : emptyTitle}
              description={hasFilters ? t("common.adjustFilters", "Try changing your search or filters.") : emptyDescription}
              action={hasFilters ? <Button variant="outline" onClick={() => setFilters({})}>{t("common.clearAll", "Clear all filters")}</Button> : emptyAction}
            />
          ) : (
            <DocumentsTable
              data={sorted}
              columnsData={columnsData}
              onSelect={onSelect}
              showSelection={showSelection}
              filters={filters}
              setFilters={setFilters}
              sort={sort}
              setSort={setSort}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function hasFilterValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}
